import { Button } from "@mantine/core";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { GetAllReportInfo } from "../../services/manageReportService";
import { GoogleSSOAuthenticationService } from "./../../services/googleAuthenticationService";
import "./manageReport.css";
import { ReportDataRow, ReportHeaderMapping } from "./type";
import googleIcon from "../../assets/image/svg/googleIcon.svg"
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import SearchBar from "material-ui-search-bar";
import ViewIcon from '@mui/icons-material/RemoveRedEyeRounded';
import EditIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import ApproveIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloneIcon from '@mui/icons-material/FileCopyRounded';
import { Tooltip } from '@mui/material';
import { ReportTransfer } from "./ReportTransfer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getNameFromEmail } from "../../services/reportService";
import { Loader } from "../../components/loader/Loader";

// SSO configuration - adjust these values to match your environment
const ssoConfig = {
  tenant: "salescode_internal",
  appId: "salescode_internal_app",
  apiBaseUrl: "https://dev-auth.salescode.ai",
  tokenEndpoint: "/v1/authenticate/sso/salescode_internal_google/token",
  userInfoEndpoint: "/v1/users/me",
};

export function ManageReport() {
  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const userRole = localStorage.getItem("G-auth-role") ;
  const [loading, setLoading] = useState(true); // Start with loading true
  const [loginError, setLoginError] = useState<string | null>(null);

  // Table state
  const [rows, setRows] = useState<ReportDataRow[]>([]);
  const [page, setPage] = useState(0); // Remove 'let' keyword
  const [originalRows, setOriginalRows] = useState<ReportDataRow[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searched, setSearched] = useState<string>("");
  const [approver, setApprover] = useState<boolean>(false);
  const [isCheckboxVisible, setIsCheckboxVisible] = useState(false);
  const [selectedData, setSelectedData] = useState<any[]>([]);

  // Dialog state
  const [openSigninModal, setOpenSigninModal] = useState(true);

  // Auth service instance - use useMemo to prevent recreation
  const authService = React.useMemo(
    () => new GoogleSSOAuthenticationService(ssoConfig),
    []
  );

  type refTYPE = {
    cloneReport: (data: any) => void;
  };
  const childRef = React.useRef<refTYPE>(null);

  // Memoized functions to prevent infinite re-renders
  function getReportInfo() {
        GetAllReportInfo().then((data) => {
            let reportInfo = data.features;
            reportInfo.map((re: any) => {
                if (re.type === "ENTITY") {
                    re["entityName"] = re.implementation;
                    re["implementation"] = ""
                } else if (re.type === "QUERY") {
                    re["queryName"] = re.implementation;
                    re["implementation"] = ""
                }
                if (!re.reviewStatus) {
                    re["reviewStatus"] = "APPROVED"
                }
                return re;
            });
            setRows(data.features)
            setOriginalRows(data.features)
        })
    }

  const isApproval = useCallback(() => {
    if(userRole === "ROLE_SUPER_ADMIN" || userRole === "ROLE_ADMIN"){
      setApprover(true);
    }
  }, [userRole]);

  const checkAuthCookie = useCallback(() => {
    try {
      // Check for existing localStorage auth
      const storedToken = localStorage.getItem("token");
      const storedEmail = localStorage.getItem("email");

      if (storedToken && storedEmail) {
        setUserEmail(storedEmail);
        setIsLoggedIn(true);
        return true;
      }

      // Check auth cookie
      const authCookie = localStorage.getItem("auth_cookie");
      if (!authCookie) {
        setOpenSigninModal(true);
        return false;
      }

      const cookieData = JSON.parse(authCookie);

      if (!cookieData.isAuthenticated) {
        setOpenSigninModal(true);
        return false;
      }

      // Check expiration
      if (cookieData.expires) {
        const expiryTime = new Date(cookieData.expires);
        const now = new Date();
        if (now > expiryTime) {
          localStorage.removeItem("auth_cookie");
          setOpenSigninModal(true);
          return false;
        }
      }

      const email = localStorage.getItem("user_email");
      if (email && cookieData.token) {

        localStorage.setItem("token", cookieData.token);
        localStorage.setItem("email", email);
        setUserEmail(email);
        setIsLoggedIn(true);
        return true;
      } else {
        getUserFromToken(cookieData.token);
        return true;
      }
    } catch (error) {
      console.error("Error parsing auth cookie:", error);
      setOpenSigninModal(true);
      return false;
    }
  }, []);

  const getUserFromToken = useCallback(
    async (token: string) => {
      try {
        const userData = await authService.getUserData(token);
        if (userData && (userData.emailId || userData.email)) {
          const email = userData.emailId || userData.email;
          localStorage.setItem("user_email", email);
          localStorage.setItem("token", token);
          localStorage.setItem("email", email);
          setUserEmail(email);
          setIsLoggedIn(true);
        } else {
          setOpenSigninModal(true);
        }
      } catch (error) {
        console.error("Error getting user data:", error);
        setOpenSigninModal(true);
      }
    },
    [authService]
  );

  const handleSSOCallback = useCallback(
    async (queryString: string) => {
      try {
        const token = await authService.getToken(queryString);

        if (token) {
          const userData = await authService.getUserData(token);

          if (userData) {
            const now = new Date();
            const expirationTime = new Date(
              now.getTime() + 12 * 60 * 60 * 1000
            );

            // Store auth cookie
            localStorage.setItem(
              "auth_cookie",
              JSON.stringify({
                isAuthenticated: true,
                token: token,
                timestamp: now.toISOString(),
                expires: expirationTime.toISOString(),
                expiresIn: "1d",
                tokenType: "bearer",
              })
            );

            
            localStorage.setItem("user_email",userData.email || userData.emailId || "");
            localStorage.setItem("token", token);
            localStorage.setItem("email",userData.email || userData.emailId || "");          
            localStorage.setItem("G-auth-role", userData.roles || "ROLE_USER");
            isApproval();
            window.history.replaceState({},document.title,window.location.pathname);
            setUserEmail(userData.email || userData.emailId || "");
            setIsLoggedIn(true);
            setOpenSigninModal(false);
          } else {
            setLoginError("Could not retrieve user information");
            setOpenSigninModal(true);
          }
        } else {
          setLoginError("Failed to authenticate");
          setOpenSigninModal(true);
        }
      } catch (error) {
        console.error("SSO callback error:", error);
        setLoginError("Authentication failed");
        setOpenSigninModal(true);
      }
    },
    [authService]
  );

  const checkAuthenticationStatus = useCallback(async () => {
    try {
      setLoading(true);

      // Check query string for SSO callback
      const queryString = window.location.search;

      if (queryString) {
        await handleSSOCallback(queryString);
      } else {
        // Check existing authentication
        checkAuthCookie();
      }
    } catch (error) {
      console.error("Error checking authentication:", error);
      setOpenSigninModal(true);
    } finally {
      setLoading(false);
    }
  }, [handleSSOCallback, checkAuthCookie]);

  // Check authentication on component mount
  useEffect(() => {
    checkAuthenticationStatus();
    // Remove handleLogout from here as it clears auth state immediately
  }, [checkAuthenticationStatus]);

  // Load report data when authenticated
  useEffect(() => {
    getReportInfo()
  }, []);

  useEffect(() => {
    isApproval()
  }, [userRole]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setLoginError(null);
      await authService.login();
    } catch (error: any) {
      setLoading(false);
      const errorMsg = error.message || "Failed to initiate login";
      setLoginError(errorMsg);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const requestSearch = (searchedVal: string) => {
    setSearched(searchedVal);
    if (searchedVal === "") {
      setRows(originalRows);
    } else {
      const filteredRows = originalRows.filter((row: any) => {
        return JSON.stringify(row)
          .toLowerCase()
          .includes(searchedVal.toLowerCase());
      });
      if(filteredRows.length > 0){
        setRows(filteredRows);
      }else{
        setRows(originalRows);
      }
    }
  };

  const cancelSearch = () => {
    setSearched("");
    setRows(originalRows);
  };

  const tableRender = () => {
    const columns: ReportHeaderMapping[] = [
      { label: "Name", id: "name", minWidth: 100 },
      { label: "Type", id: "type", minWidth: 70 },
      { label: "Description", id: "description", minWidth: 100 },
      { label: "Status", id: "reviewStatus", minWidth: 100 },
      { label: "EntityName", id: "entityName", minWidth: 100 },
      { label: "QueryName", id: "queryName", minWidth: 100 },
      { label: "Implementation", id: "implementation", minWidth: 70 },
      { label: "action", id: "id", minWidth: 50 },
    ];

    const hasValidData =
      rows &&
      rows.length > 0 &&
      rows[0].id !== "emptyRow" &&
      rows[0].id !== "errorRow";

    if (!hasValidData) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Manage Report Infos</h1>
          <div style={{ marginTop: "50px" }}>
            {rows.length > 0 && rows[0].id === "errorRow"
              ? "Error loading report data. Please try again later."
              : "No report data available."}
          </div>
        </div>
      );
    }
    const handleLogoutClick = () => {
    localStorage.removeItem('email');
    localStorage.removeItem('token');
    localStorage.removeItem('auth_cookie');
    localStorage.removeItem('user_email');
    localStorage.removeItem('machineId');
    localStorage.removeItem('G-auth-role');
    
    setIsLoggedIn(false);
    setUserEmail('');
    setOpenSigninModal(true);
  };

    return (
      <div>
        <div
          className="header-container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "5px",
            paddingRight: "10px",
            width: "100%",
            boxShadow:"none",
            background:"none",
            margin:"0"
          }}
        >
          <h1 className="report-header">Manage Report Infos</h1>
          <div style={{ display: "flex", alignItems: "center",  gap: "10px", paddingTop:"5px" }}>
            <span style={{fontWeight:500}}>Welcome, {getNameFromEmail(true)}!</span>
            <Button onClick={handleLogoutClick} color="red"> Logout </Button>
          </div>
        </div>

        <div className={"manage-report-buttons"} style={{marginBottom:"15px"}}>
          <Button
            onClick={(e: any) => {
              e.preventDefault();
              window.location.href = "/dashboard/manageReport/report";
            }}
            color="teal"
            style={{marginLeft:"7px", marginTop:"30px"}}
          >
            Create Report
          </Button>
          <ReportTransfer
            rows={rows}
            setRows={setRows}
            originalRows={originalRows}
            setOriginalRows={setOriginalRows}
            setIsCheckboxVisible={setIsCheckboxVisible}
            selectedData={selectedData}
            ref={childRef}
          />
          <SearchBar
                value={searched}
                onChange={(searchVal) => requestSearch(searchVal)}
                onCancelSearch={() => cancelSearch()}
                className="search-bar"
                style={{marginTop:"26px", height:"40px", marginRight:"4px" }}
          />
        </div>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <div id="tableContainer">
            <TableContainer sx={{ maxHeight: '100%' }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow key="manageReportHeader">
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{
                          minWidth: column.minWidth,
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          background: "#12b886",
                          color: "white",
                          textAlign:"left",
                          
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      (row: {
                        [x: string]: any;
                        id: React.Key | null | undefined;
                      }) => {
                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={row["id"]}
                          >
                            {columns.map((column) => {
                              if (column.label === "action") {
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {isCheckboxVisible ? (
                                      <div style={{ display: 'flex', justifyContent:'center' }}>
                                        <Tooltip title="Clone Report">
                                          <CloneIcon
                                            onClick={() => {
                                              if (childRef.current) {
                                                childRef.current.cloneReport(row);
                                              }
                                            }}
                                            fontSize="medium"
                                            style={{ cursor: 'pointer' }}
                                            sx={{ color: '#12b886' }}
                                            />
                                          </Tooltip>
                                      </div>
                                    ) : (
                                      <div
                                        style={{ display: "flex", gap: "8px" }}
                                      >
                                        <Tooltip title="Edit Report">
                                        <EditIcon
                                          onClick={(e: any) => {
                                            e.preventDefault();
                                            window.location.href ="/dashboard/manageReport/report?id=" +row["id"];
                                          }}
                                          fontSize="medium"
                                          style={{ cursor: 'pointer' }}
                                          sx={{ color: '#12b886' }}
                                        />
                                        </Tooltip>
                                        <Tooltip title="View Report">
                                        <ViewIcon
                                        titleAccess="View Report"
                                          onClick={(e: any) => {
                                            e.preventDefault();
                                            window.location.href = "/dashboard/manageReport/report?view=true&id=" + row["id"];
                                          }}
                                          fontSize="medium"
                                          style={{ cursor: 'pointer' }}
                                          sx={{ color: '#12b886' }}
                                        />
                                      </Tooltip>

                                        {approver &&
                                          row["reviewStatus"] === "PENDING" && (
                                            <Tooltip title="Approve Report">
                                            <ApproveIcon
                                              titleAccess="Approve Report"
                                              onClick={(e: any) => {
                                                e.preventDefault();
                                                window.location.href ="manageReport/report?review=true&id=" +row["id"];
                                              }}
                                              fontSize="medium"
                                              style={{ cursor: 'pointer' }}
                                              sx={{ color: '#FF5F1F' }}
                                            />
                                            </Tooltip>
                                          )}
                                      </div>
                                    )}
                                  </TableCell>
                                );
                              } else {
                                const value = row[column.id];
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value || "-"}
                                  </TableCell>
                                );
                              }
                            })}
                          </TableRow>
                        );
                      }
                    )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10,25,50,75,100]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              SelectProps={{
                native: true, // if true, uses native <select>
                sx: {
                  padding: '4px 8px',
                  fontSize: '14px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                },
              }}
              
            />
          </div>
        </Paper>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          justifyItems:"center",
          height:"100%"
        }}
      >
      <Loader/>
      </div>
    );
  }

  return (
    <div>
      {!isLoggedIn ? (
        <Dialog open={openSigninModal} onClose={() => setOpenSigninModal(true)}>
          <DialogTitle>
            Authentication Required
            <IconButton
              aria-label="close"
              onClick={() => setOpenSigninModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loginError && (
              <div style={{ color: "red", marginBottom: "16px" }}>
                Error: {loginError}
              </div>
            )}
            <p>Please sign in to access the report management system.</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLogin} color="teal" disabled={loading} leftIcon={
        <img
          src={googleIcon}
          alt="Google logo"
          style={{ width: 20, height: 20 }}
        />
      }>
              {loading ? "Signing in..." : "Sign in with Google"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        tableRender()
      )}
    </div>
  );
}
